import { npcs } from '../data/guide/npcs'
import { recipes, recipePath } from '../data/guide/recipes'
import { characterClasses } from '../data/guide/classes'
import { publishedQuestIds, questExpectations, questTitle } from '../data/guide/quests'
import { adminConfirmation, formatAdminDate } from '../data/guide/adminConfirmations'
import { inGameTest, inGameTests } from '../data/guide/inGameTests'
import { EXPORT_DATE_LABEL } from '../data/world/package'
import { num, str, type Row } from './csv'
import { buildLookup } from './progressionLookup'
import { positionsFor, type EventPosition } from './eventPositions'
import { isPermanentSpawn, npcAdminKey, npcLocationText, resolveNpcLocation } from './npcLocation'
import { buildQuestView } from './questView'
import { itemCoverage, resolveItemSources } from './itemSources'
import { auditInvasions } from './invasionValidation'
import { coordLabel, itemPath, monsterPath } from './worldFormat'
import type { Material } from '../types/progression'
import type { WorldData } from '../types/world'

/*
 * Lista consolidada de TODO lo que falta confirmar en la guía, calculada con los archivos cargados.
 * Un pendiente = una pregunta. Si el mismo dato aparece en varias fichas, se pregunta una vez.
 * Usa la misma lógica que las fichas (resolveItemSources + itemCoverage).
 */

export type PendingCategory = 'ubicacion' | 'mision' | 'obtencion' | 'receta' | 'invasion' | 'evento' | 'mapa' | 'monstruo' | 'paquete' | 'sistema'
export type PendingReason = 'falta-dato' | 'conexion' | 'conflicto' | 'clasica' | 'en-revision'
export type Evidence = 'administración' | 'captura' | 'configuración' | 'prueba en el juego'

export type Pending = {
  key: string
  category: PendingCategory
  section: string
  links: string[]
  name: string
  missing: string
  known: string
  reason: PendingReason
  question: string
  evidence: Evidence[]
  affects?: number
  /** Necesita respuesta del administrador (no es un problema interno de conexión). */
  needsAdmin: boolean
}

export type ProposedTest = { testId: string; what: string; how: string; record: string }
export type SectionCoverage = { section: string; reviewed: boolean; note: string }

/** Datos del paquete que el mundo cargado no incluye y se leen solo para este informe. */
export type PendingExtras = {
  eventMonsters?: Row[]
  eventSpawnRules?: Row[]
  /** Posiciones resueltas con utils/eventPositions (la misma lógica que las fichas). */
  positions?: EventPosition[]
  controlPending?: unknown
  errors: string[]
}

const categoryOrder: PendingCategory[] = ['ubicacion', 'mision', 'obtencion', 'receta', 'invasion', 'evento', 'mapa', 'monstruo', 'paquete', 'sistema']
export const categoryLabel: Record<PendingCategory, string> = {
  ubicacion: 'Ubicaciones de NPC',
  mision: 'Misiones y evoluciones',
  obtencion: 'Obtención de ítems',
  receta: 'Recetas, alas y capas',
  invasion: 'Invasiones',
  evento: 'Eventos',
  mapa: 'Accesos a mapas',
  monstruo: 'Monstruos y apariciones',
  paquete: 'Declarados en 00_control.csv',
  sistema: 'Sistemas y secciones sin datos',
}
export const reasonLabel: Record<PendingReason, string> = {
  'falta-dato': 'Falta el dato en los archivos',
  conexion: 'Relación entre archivos sin resolver',
  conflicto: 'Fuentes contradictorias',
  clasica: 'Referencia clásica sin validar en Vanguard',
  'en-revision': 'En revisión: datos presentes, publicación retenida',
}

const list = (xs: string[], n = 6) => (xs.length > n ? `${xs.slice(0, n).join(', ')} y ${xs.length - n} más` : xs.join(', '))
const isFalse = (v?: string) => /^(false|0)$/i.test(v ?? '')

type Gap = { monsterId: number; mapId?: number; monster: string; map: string; eventIds: Set<string>; items: Set<string> }

export function buildGuidePendings(w: WorldData, extras: PendingExtras = { errors: [] }) {
  const l = buildLookup(w)
  const out: Pending[] = []
  const resolvedByAdmin: { p: Pending; answer: string; date: string }[] = []
  const solvedFromData: string[] = []
  const add = (p: Omit<Pending, 'needsAdmin'> & { needsAdmin?: boolean }) => {
    if (out.some((x) => x.key === p.key)) return
    const full: Pending = { ...p, needsAdmin: p.needsAdmin ?? (p.reason !== 'conexion' && p.evidence.includes('administración')) }
    const a = adminConfirmation(p.key)
    if (a) resolvedByAdmin.push({ p: full, answer: a.answer, date: a.date })
    else out.push(full)
  }
  const appendKnown = (key: string, text: string) => {
    const p = out.find((x) => x.key === key)
    if (p) p.known += ` ${text}`
    return !!p
  }
  const monsterName = (id: number) => w.monsters.get(id)?.name ?? `Monstruo ${id}`
  const mapName = (id?: number) => (id === undefined ? 'mapa sin indicar' : w.maps.get(id)?.name ?? `Mapa ${id}`)
  const eventName = (id: string) => w.events.get(id)?.name ?? id

  // ── 1. Ubicaciones de NPC ────────────────────────────────────────────────
  // Misma resolución que la tarjeta del NPC, las misiones, las recetas y el buscador (utils/npcLocation).
  npcs.forEach((n) => {
    const loc = resolveNpcLocation(n, { status: 'ready', data: w })
    if (loc.state === 'config' || loc.state === 'admin') {
      solvedFromData.push(`${n.name}: ${npcLocationText(loc)}.`)
      return
    }
    const presence = n.monsterId !== undefined ? l.npc(n.monsterId) : undefined
    const temporal = presence ? presence.spawns.filter((s) => !isPermanentSpawn(s)).length : 0
    const ref = loc.state === 'reference' ? loc.reference : undefined
    const multi = (ref?.points.length ?? 0) > 1
    const refText = ref ? `${ref.points.map((p) => `${p.mapName}${p.coords ? ` ${p.coords}` : ''}`).join(' · ')} (${ref.source}${ref.url ? ` — ${ref.url}` : ''})` : ''
    if (n.monsterId === undefined) return // Pet Trainer: se pregunta en npc-service:pet-trainer (NPC + ubicación juntos)
    add({
      key: npcAdminKey(n),
      category: 'ubicacion',
      section: 'NPC',
      links: [n.sectionPath, monsterPath(n.monsterId), `buscador ("${n.aliases[0]}")`],
      name: `${presence?.name ?? n.name} (monster_id ${n.monsterId})`,
      missing: ref?.points.some((p) => !p.coords) ? 'Coordenadas X/Y en Vanguard (la referencia solo indica el mapa)' : 'Validar mapa y coordenadas X/Y en Vanguard',
      known: `${presence ? 'Identidad en 06_monsters' : 'No encontrado en 06_monsters'}; sin apariciones permanentes en 08_spawns${temporal ? ` (tiene ${temporal} apariciones de invasión/evento, no válidas como ubicación fija)` : ''}. ${ref ? `Referencia mostrada a los jugadores: ${refText}${ref.condition ? `. ${ref.condition}` : ''}` : 'Sin referencia disponible: la guía muestra "Ubicación no documentada".'}`,
      reason: 'falta-dato',
      question: multi
        ? `¿En cuáles de estos mapas aparece ${n.name} en Vanguard (${ref!.points.map((p) => `${p.mapName}${p.coords ? ` ${p.coords}` : ''}`).join('; ')}), con qué X/Y, y se desplaza entre ellos o está fijo?`
        : ref
          ? `¿${n.name} está en ${ref.points[0].mapName}${ref.points[0].coords ? ` (${ref.points[0].coords})` : ''} en Vanguard? Si no, ¿en qué mapa y X/Y?`
          : `¿En qué mapa y coordenadas X/Y está ${n.name} en Vanguard?`,
      evidence: ['administración', 'captura'],
    })
  })

  // ── 2. Misiones ──────────────────────────────────────────────────────────
  const gaps = new Map<string, Gap>()
  const addGap = (monsterId: number, mapId: number | undefined, eventId: string | undefined, item: string) => {
    const k = `${monsterId}|${mapId ?? ''}`
    const g = gaps.get(k) ?? { monsterId, mapId, monster: monsterName(monsterId), map: mapName(mapId), eventIds: new Set<string>(), items: new Set<string>() }
    if (eventId) g.eventIds.add(eventId)
    g.items.add(item)
    gaps.set(k, g)
  }
  publishedQuestIds.forEach((qid) => {
    const v = buildQuestView(w, qid)
    const title = questTitle(qid)
    const path = qid <= 1 ? '/guia/evoluciones#sebina' : qid <= 3 ? '/guia/evoluciones#marlon' : '/guia/evoluciones#tercera'
    if (!v) {
      add({ key: `quest-missing:${qid}`, category: 'mision', section: 'Evoluciones', links: [path], name: title, missing: 'La misión completa', known: 'No figura en 36_quests.', reason: 'conexion', question: `Relación interna: la misión ${qid} no aparece en 36_quests.`, evidence: ['configuración'] })
      return
    }
    const exp = questExpectations[qid]
    if (exp && v.levelMin !== undefined && exp.levelMin !== v.levelMin) {
      add({ key: `quest-level-conflict:${qid}`, category: 'mision', section: 'Evoluciones', links: [path], name: title, missing: 'Nivel mínimo correcto', known: `Archivos: ${v.levelMin}. Referencia del staff: ${exp.levelMin}.`, reason: 'conflicto', question: `¿Cuál es el nivel mínimo vigente para ${title}: ${v.levelMin} o ${exp.levelMin}?`, evidence: ['administración', 'prueba en el juego'] })
    }
    if (exp && v.zen !== undefined && exp.zen !== v.zen) {
      add({ key: `quest-zen-conflict:${qid}`, category: 'mision', section: 'Evoluciones', links: [path], name: title, missing: 'Zen correcto', known: `Archivos: ${v.zen}. Referencia del staff: ${exp.zen}.`, reason: 'conflicto', question: `¿Cuánto Zen cuesta hoy ${title}: ${v.zen} o ${exp.zen}?`, evidence: ['administración', 'prueba en el juego'] })
    }
    const reqMissing = [v.levelMin === undefined && 'nivel mínimo', v.zen === undefined && 'Zen'].filter(Boolean).join(' y ')
    if (reqMissing) {
      add({ key: `quest-req:${qid}`, category: 'mision', section: 'Evoluciones', links: [path], name: title, missing: reqMissing, known: 'La misión existe en 36_quests/37_quest_rules sin ese valor.', reason: 'falta-dato', question: `¿Qué ${reqMissing} exige ${title}?`, evidence: ['administración', 'configuración'] })
    }
    v.kills.forEach((k) => {
      if (!k.resolved || k.quantity === undefined) {
        add({ key: `quest-kill:${qid}:${k.objective.id}`, category: 'mision', section: 'Evoluciones', links: [path], name: `${title}: ${k.name}`, missing: [!k.resolved && 'qué monstruo es', k.quantity === undefined && 'cantidad de bajas'].filter(Boolean).join(' y '), known: `Objetivo ${k.objective.id} de 39_quest_objectives.`, reason: k.resolved ? 'falta-dato' : 'conexion', question: k.resolved ? `En ${title}, ¿cuántas veces hay que derrotar a ${k.name}?` : `Relación interna: el monstruo del objetivo ${k.objective.id} no se resuelve en 06_monsters.`, evidence: k.resolved ? ['administración', 'configuración'] : ['configuración'] })
      }
    })
    v.items.forEach((it) => {
      const id = it.variantId ?? it.itemId
      if (it.quantity === undefined) {
        add({ key: `quest-item-qty:${qid}:${it.itemId}`, category: 'mision', section: 'Evoluciones', links: [path, itemPath(id)], name: `${title}: ${it.name}`, missing: 'Cantidad requerida', known: `Ítem ${id} en 39_quest_objectives sin cantidad.`, reason: 'falta-dato', question: `¿Cuántas unidades de ${it.name} pide ${title}?`, evidence: ['administración', 'configuración'] })
      }
      if (!it.drop || !it.drop.targets.length) {
        add({ key: `quest-item-source:${id}`, category: 'mision', section: 'Evoluciones', links: [path, itemPath(id)], name: it.name, missing: 'Monstruo y mapa donde se consigue con la misión activa', known: it.drop ? 'Tiene regla en 41_quest_drop_rules sin objetivos en 42 ni en 11_route_targets.' : 'Sin regla en 41_quest_drop_rules.', reason: it.drop ? 'conexion' : 'falta-dato', question: it.drop ? `Relación interna: la regla de drop de ${it.name} no tiene objetivos.` : `¿Qué monstruos sueltan ${it.name} durante ${title} y en qué mapas?`, evidence: it.drop ? ['configuración'] : ['administración', 'configuración'] })
      } else {
        it.drop.targets.filter((t) => !t.spawns.length).forEach((t) => addGap(t.monsterId, t.mapId, undefined, `${it.name} (misión)`))
      }
    })
  })
  ;([
    ['quest-reward:sebina', 'Sebina (misiones 0 y 1)', '/guia/evoluciones#sebina', '¿Qué entrega completar la misión de Sebina en Vanguard (cambio a segunda clase, otros premios)?'],
    ['quest-reward:marlon', 'Marlon, primera parte (misión 2)', '/guia/evoluciones#marlon', '¿Qué beneficio da completar Marlon en Vanguard (puntos por nivel u otro) y desde qué nivel aplica?'],
    ['quest-reward:devin', 'Tercera evolución (misiones 4–6)', '/guia/evoluciones#tercera', '¿Qué entrega cada etapa de Devin y en cuál se obtiene la tercera clase?'],
  ] as const).forEach(([key, name, path, question]) => add({ key, category: 'mision', section: 'Evoluciones', links: [path], name, missing: 'Recompensa o efecto de la misión', known: 'Requisitos y objetivos están en los archivos; ningún archivo del paquete contiene recompensas de misión.', reason: 'falta-dato', question, evidence: ['administración', 'prueba en el juego'] }))
  add({ key: 'combo:bk', category: 'mision', section: 'Evoluciones', links: ['/guia/evoluciones#combo', 'buscador ("combo bk")'], name: 'Combo del Blade Knight (misión 3)', missing: 'Secuencia de habilidades y condiciones del combo', known: 'Requisitos de la misión 3 en los archivos. La secuencia no está en ningún archivo.', reason: 'falta-dato', question: '¿Qué secuencia de habilidades activa el combo en Vanguard, con qué tiempos, y aplica solo al Blade Knight?', evidence: ['administración', 'prueba en el juego'] })
  add({ key: 'quest-unpublished:7-9', category: 'mision', section: 'Evoluciones', links: ['/guia/verificacion#progresion'], name: 'Misiones 7, 8 y 9', missing: 'Qué son y si deben publicarse', known: 'Existen en los archivos de misiones sin título editorial; el panel de misiones muestra sus referencias.', reason: 'falta-dato', question: '¿Qué misiones son la 7, 8 y 9, están activas y deben aparecer en la guía?', evidence: ['administración'] })
  characterClasses.forEach((c) => {
    if (!c.sebinaItem) return
    const id = l.identify(c.sebinaItem)
    if (!id) add({ key: `name:${c.sebinaItem.gameName}`, category: 'mision', section: 'Evoluciones', links: ['/guia/evoluciones#sebina'], name: `${c.sebinaItem.gameName} (${c.base})`, missing: 'Nombre e identificador en el catálogo', known: 'Probado por ID, nombre, alias y variantes sin coincidencia.', reason: 'falta-dato', question: `¿Con qué nombre exacto (o item_id) figura en Vanguard el objeto de Sebina para ${c.base} (clásico: ${c.sebinaItem.gameName})?`, evidence: ['administración', 'configuración'] })
    else solvedFromData.push(`${c.sebinaItem.gameName}: identificado como ${id.name} (${id.id}) por ${id.matchedBy}.`)
  })

  // ── 3. Recetas (cada variante por separado) ──────────────────────────────
  // Trainer (226): el staff confirmó que "Pet Trainer" y "Trainer" son el mismo NPC; su ubicación se
  // resuelve en el bloque 1 como cualquier otro NPC (apariciones permanentes de 226 o referencia).
  solvedFromData.push('Trainer (226): confirmado por el staff como el NPC que resucita Spirit of Dark Horse y Spirit of Dark Raven ("Pet Trainer" es el mismo NPC).')
  // Capas: existencia y compatibilidad salen de 01_items y 05_item_classes; solo se pregunta por la receta.
  const capeInfo = (id: string) => {
    const it = w.items.get(id)
    const cls = Array.from(new Set(w.itemClasses.get(id) ?? [])).map((c) => w.classNames.get(c) ?? `clase ${c}`)
    return it ? `${it.name} (${id})${cls.length ? ` · 05_item_classes: ${cls.join(', ')}` : ' · sin filas en 05_item_classes'}` : `${id} no encontrado en 01_items`
  }
  solvedFromData.push(`Capas documentadas: ${['12,40', '12,50', '12,49'].map(capeInfo).join(' | ')}.`)
  add({
    key: 'recipe:capes-third',
    category: 'receta',
    section: 'Alas y capas',
    links: [recipePath('alas-evo-3'), itemPath('12,40'), itemPath('12,50')],
    name: 'Receta de las capas de tercera generación (Cape of Emperor y Lord of the Cape)',
    missing: 'Receta y condiciones: ingredientes, mínimos, Zen, probabilidad, fallo y si sale de la mezcla de alas Evo 3',
    known: `Existencia y compatibilidad documentadas: ${capeInfo('12,40')}; ${capeInfo('12,50')}.`,
    reason: 'clasica',
    question: '¿Cape of Emperor (12,40, Lord Emperor) y Lord of the Cape (12,50, Fist Master) se crean con la misma mezcla de alas Evo 3? ¿Qué ingredientes, mínimos, Zen, probabilidad y efectos del fallo tiene cada una?',
    evidence: ['administración', 'configuración', 'prueba en el juego'],
  })
  add({
    key: 'recipe:cloak-of-warrior',
    category: 'receta',
    section: 'Alas y capas',
    links: [recipePath('capas'), itemPath('12,49')],
    name: 'Receta de Cloak of Warrior (12,49)',
    missing: 'Cómo se obtiene o crea y con qué condiciones',
    known: `Documentado: ${capeInfo('12,49')}.`,
    reason: 'clasica',
    question: '¿Cloak of Warrior (12,49) se crea en la Chaos Machine? Si sí: ingredientes, mínimos, Zen, probabilidad y efectos del fallo. No se asume que use la receta de la capa del Dark Lord.',
    evidence: ['administración', 'configuración', 'prueba en el juego'],
  })
  add({
    key: 'recipe:jewel-packs',
    category: 'receta',
    section: 'Mascotas y combinaciones',
    links: ['/guia/combinaciones'],
    name: 'Paquetes de joyas (Bless, Soul, Life…)',
    missing: 'Si existen en Vanguard, nombre e item_id de cada paquete, NPC que los arma y cantidades',
    known: 'Las recetas clásicas usan "paquete de 10"; el nombre del paquete no se identificó en el catálogo.',
    reason: 'falta-dato',
    question: '¿Se pueden empaquetar joyas en Vanguard? Si sí: ¿con qué NPC, en paquetes de cuántas y con qué nombre/item_id aparecen?',
    evidence: ['administración', 'configuración'],
  })
  const craftKinds = Array.from(new Set(w.routes.map((r) => r.kind))).filter((k) => /mix|craft|chaos|combin|recipe/i.test(k))
  const recipeData = craftKinds.length ? `Hay rutas de tipo ${craftKinds.join(', ')} en 10_drop_routes por revisar.` : 'Ningún archivo del paquete contiene recetas de la Chaos Machine.'
  const unknownNames = new Map<string, string[]>()
  const checkMat = (m: Material, where: string) => {
    if (!m.gameName && !m.itemId) return
    if (!l.identify(m)) unknownNames.set(m.gameName ?? m.itemId!, [...(unknownNames.get(m.gameName ?? m.itemId!) ?? []), where])
  }
  const matText = (mats: Material[]) => mats.map((m) => `${m.qty ?? '?'} × ${m.gameName ?? m.esName}${m.requirement ? ` (${m.requirement})` : ''}`).join('; ')
  recipes.forEach((r) => {
    const units = r.variants?.length
      ? r.variants.map((v) => ({ key: `recipe:${r.id}:${v.id}`, title: v.title, mats: v.ingredients, results: v.results, extra: v.note }))
      : [{ key: `recipe:${r.id}`, title: r.title, mats: r.ingredients, results: r.results, extra: undefined as string | undefined }]
    units.forEach((u) => {
      u.mats.forEach((m) => checkMat(m, u.title))
      add({
        key: u.key,
        category: 'receta',
        section: !r.group || r.group === 'alas' ? 'Alas y capas' : 'Mascotas y combinaciones',
        links: [recipePath(r.id), !r.group || r.group === 'alas' ? '/guia/alas' : '/guia/combinaciones', 'buscador'],
        name: `Receta: ${u.title}`,
        missing: `Por separado: ingredientes y cantidades vigentes, Zen, probabilidad de éxito, efectos del fallo, cambio por VIP y NPC que realiza la combinación (${r.machine ?? 'Chaos Machine'})`,
        known: `Referencia clásica: ${matText(u.mats) || 'sin ingredientes cargados'}.${u.extra ? ` ${u.extra}` : ''} ${recipeData}`,
        reason: 'clasica',
        question:
          r.id === 'capas' && u.key.endsWith(':rf')
            ? '¿Qué receta usa la capa del Rage Fighter en Vanguard (ingredientes, mínimos, Zen, éxito y fallo)? No se asume que sea la misma del Dark Lord.'
            : `Para "${u.title}" en Vanguard: 1) ¿se mantienen estos ingredientes y cantidades? 2) ¿cuánto Zen cuesta? 3) ¿cuál es la probabilidad de éxito? 4) ¿qué se pierde si falla? 5) ¿el VIP cambia la probabilidad? 6) ¿la hace ${r.machine ?? 'la Chaos Machine'}?`,
        evidence: ['administración', 'configuración', 'prueba en el juego'],
      })
      u.results.forEach((res) => {
        if (l.identify(res)) return
        unknownNames.set(res.gameName, [...(unknownNames.get(res.gameName) ?? []), `${u.title} (resultado)`])
      })
    })
  })
  unknownNames.forEach((where, name) => add({ key: `name:${name}`, category: 'receta', section: 'Alas y capas', links: ['/guia/alas', 'buscador'], name, missing: 'Nombre e identificador en el catálogo', known: `Usado en: ${list(Array.from(new Set(where)))}. Sin coincidencia por ID, nombre, alias ni variante.`, reason: 'falta-dato', question: `¿Con qué nombre exacto o item_id figura "${name}" en el catálogo de Vanguard, o no existe en el servidor?`, evidence: ['administración', 'configuración'] }))

  // ── 4. Obtención de ítems (misma lógica que las fichas) ──────────────────
  const byReason = new Map<string, { ids: string[]; names: string[]; reason: string; detail: string }>()
  const noRoutes: string[] = []
  const reviewItems: string[] = []
  const coordItems: string[] = []
  w.items.forEach((it) => {
    const sources = resolveItemSources(w, it.id)
    const c = itemCoverage(sources)
    const walkTargets = (ss: typeof sources) =>
      ss.filter((s) => !s.blocked && s.status !== 'disabled').forEach((s) => {
        s.targets.filter((t) => !t.spawns.length).forEach((t) => addGap(t.monsterId, t.mapId, t.eventId, it.name))
      })
    walkTargets(sources)
    if (c.coverage === 'no-routes') noRoutes.push(`${it.name} (${it.id})`)
    else if (c.coverage === 'review') reviewItems.push(`${it.name} (${it.id})`)
    else if (c.coverage === 'partial' && c.detail === 'coords') coordItems.push(`${it.name} (${it.id})`)
    else if (c.coverage === 'partial') {
      const pattern = (c.reason ?? '—').replace(/route:\w+|rule:\d+|\d+,\d+(@\d+)?|\d+ objetivos|drop_level del ítem [\d—]+|\(.*?\)/g, '…')
      const g = byReason.get(pattern) ?? { ids: [], names: [], reason: c.reason ?? '—', detail: c.detail ?? 'missing' }
      g.ids.push(it.id)
      g.names.push(it.name)
      byReason.set(pattern, g)
    }
  })
  Array.from(byReason.entries()).sort((a, b) => b[1].ids.length - a[1].ids.length).forEach(([pattern, g], i) => {
    const disabledBox = /desactivadas por configuración/.test(g.reason)
    const noBoxRoutes = /no tiene rutas en 10_drop_routes/.test(g.reason)
    const internal = /inexistente|no existe|sin filas en route_targets.*sin via_item_ids/i.test(g.reason)
    add({
      key: `items-partial:${g.detail}:${i}`,
      category: 'obtencion',
      section: 'Ítems y drops',
      links: g.ids.slice(0, 3).map(itemPath),
      name: `${g.ids.length} ítems: ${pattern.slice(0, 90)}`,
      missing: disabledBox ? 'Si la caja intermedia se puede conseguir hoy' : noBoxRoutes ? 'Cómo se consigue la caja intermedia' : 'Monstruo, evento o caja de origen',
      known: `Ejemplos: ${list(g.names)}. Motivo calculado: ${g.reason}`,
      reason: internal ? 'conexion' : 'falta-dato',
      question: internal
        ? 'Relación interna entre archivos: la reviso con el diagnóstico.'
        : disabledBox
          ? `La caja de la que salen ${list(g.names, 3)} solo tiene rutas desactivadas en la configuración. ¿Esa caja se consigue hoy de alguna otra forma, o estos ítems no se obtienen por esa vía?`
          : `¿Cómo se obtienen en Vanguard ${list(g.names, 4)}? (lista completa en el anexo)`,
      evidence: internal ? ['configuración'] : ['administración', 'configuración'],
      affects: g.ids.length,
    })
  })
  if (noRoutes.length) {
    add({ key: 'items-no-routes', category: 'obtencion', section: 'Ítems y drops', links: ['/guia/items'], name: `${noRoutes.length} ítems publicados sin ruta de drop en los archivos`, missing: 'Si se obtienen por otra vía (tienda, NPC, cash shop, recompensa, receta) o no están disponibles', known: `Ejemplos: ${list(noRoutes)}. Sin filas en 10_drop_routes. "Sin ruta de drop" no significa "no disponible en el servidor".`, reason: 'falta-dato', question: '¿Estos ítems se consiguen de otra forma en Vanguard (tienda, NPC, cash shop, evento, receta) o no están disponibles en el servidor? Lista completa en el anexo A.', evidence: ['administración'], affects: noRoutes.length })
  }

  // ── 5. Monstruo + mapa sin coordenadas, por contexto (31/32) ─────────────
  const evMon = extras.eventMonsters ?? []
  const evRules = extras.eventSpawnRules ?? []
  const byEvent = new Map<string, { gaps: Gap[]; inactive: boolean }>()
  const ordinaryGaps: Gap[] = []
  let solvedBy32 = 0
  const allPositions = extras.positions ?? []
  gaps.forEach((g) => {
    // Misma resolución que las fichas: monstruo + mapa + evento del objetivo (si lo tiene).
    const evs = g.eventIds.size ? Array.from(g.eventIds) : [undefined]
    if (evs.some((eid) => positionsFor(allPositions, g.monsterId, g.mapId, eid).length)) {
      solvedBy32++
      return
    }
    const mons = evMon.filter((r) => num(r.monster_id) === g.monsterId && (g.mapId === undefined || num(r.map_id) === undefined || num(r.map_id) === g.mapId))
    const evIds = new Set<string>([...g.eventIds, ...mons.map((r) => str(r.event_id)).filter((x): x is string => !!x)])
    if (!evIds.size) {
      ordinaryGaps.push(g)
      return
    }
    evIds.forEach((eid) => {
      const e = byEvent.get(eid) ?? { gaps: [], inactive: false }
      e.gaps.push(g)
      if (w.events.get(eid)?.activeConfigured === false || mons.some((r) => str(r.event_id) === eid && isFalse(r.active))) e.inactive = true
      byEvent.set(eid, e)
    })
  })
  if (solvedBy32) solvedFromData.push(`${solvedBy32} pares monstruo + mapa sin aparición permanente tienen posición en 32_event_spawn_rules; las fichas las muestran como "Posición dentro del evento", no como spot permanente.`)
  byEvent.forEach((e, eid) => {
    add({
      key: `event-positions:${eid}`,
      category: 'monstruo',
      section: 'Eventos',
      links: ['/guia/eventos'],
      name: `${eventName(eid)}: ${e.gaps.length} monstruos sin posición`,
      missing: 'Posiciones, áreas o regla de aparición dentro del evento',
      known: `Relacionados en 31_event_monsters: ${list(e.gaps.map((g) => `${g.monster} (${g.map})`))}. Sin coordenadas en 08_spawns ni 32_event_spawn_rules.${e.inactive ? ' El evento o esas filas figuran como inactivos.' : ''}`,
      reason: 'falta-dato',
      question: e.inactive
        ? `${eventName(eid)} figura inactivo en la configuración. ¿Está activo hoy? Si lo está, ¿dónde aparecen sus monstruos (posición, área o aparición aleatoria)?`
        : `Dentro de ${eventName(eid)}, ¿los monstruos aparecen en posiciones fijas, en un área o al azar por oleadas? Si son fijas, ¿cuáles?`,
      evidence: ['administración', 'configuración'],
      affects: e.gaps.length,
    })
  })
  if (ordinaryGaps.length) {
    add({ key: 'coords-missing', category: 'monstruo', section: 'Mapas y spots', links: ordinaryGaps.slice(0, 3).map((g) => monsterPath(g.monsterId)), name: `${ordinaryGaps.length} monstruos + mapa sin coordenadas ni evento asociado`, missing: 'Coordenadas o condición de aparición', known: `Ejemplos: ${list(ordinaryGaps.map((g) => `${g.monster} en ${g.map}`))}. Tienen drops documentados; no figuran en 08_spawns, 31 ni 32 para ese mapa.`, reason: 'falta-dato', question: '¿Estos monstruos aparecen en esos mapas (spot fijo, invocación, otro evento)? Si tienen spot, ¿en qué coordenadas? (anexo D)', evidence: ['administración', 'configuración'], affects: ordinaryGaps.length })
  }
  const unplaced = Array.from(w.monsters.values()).filter((m) => (w.idx.routeTargetsByMonster.get(m.id)?.length ?? 0) > 0 && !w.idx.spawnsByMonster.get(m.id)?.length && !w.idx.locationsByMonster.get(m.id)?.length && !evMon.some((r) => num(r.monster_id) === m.id))
  if (unplaced.length) {
    add({ key: 'monsters-unplaced', category: 'monstruo', section: 'Monstruos', links: unplaced.slice(0, 3).map((m) => monsterPath(m.id)), name: `${unplaced.length} monstruos con drops pero sin ubicación ni evento`, missing: 'Dónde aparecen', known: `Ejemplos: ${list(unplaced.map((m) => `${m.name} (${m.id})`))}. En 11_route_targets; no en 08, 09 ni 31.`, reason: 'falta-dato', question: '¿Dónde aparecen estos monstruos (mapa, evento o invocación), o están desactivados en Vanguard?', evidence: ['administración', 'configuración'], affects: unplaced.length })
  }

  // ── 6. Invasiones ────────────────────────────────────────────────────────
  auditInvasions(w).forEach((a) => {
    if (!a.unlisted && !a.conflict) return
    const unlistedNames = Array.from(new Set(a.rows.filter((r) => r.verdict === 'unlisted').map((r) => r.name)))
    const conflictNames = Array.from(new Set(a.rows.filter((r) => r.verdict === 'conflict').map((r) => r.name)))
    add({
      key: `invasion:${a.policy.guideEntryId}`,
      category: 'invasion',
      section: 'Invasiones',
      links: ['/guia/invasiones-bosses/invasiones', '/guia/eventos'],
      name: `Recompensas de ${a.policy.name}`,
      missing: a.policy.missing ?? 'Lista vigente de ítems (item_id + nivel)',
      known: `Aprobado: ${a.policy.approved} Exportación: ${a.items} ítems · ${a.authorized} autorizados · ${a.unlisted} sin lista vigente${unlistedNames.length ? ` (${list(unlistedNames, 4)})` : ''} · ${a.conflict} en conflicto${conflictNames.length ? ` (${list(conflictNames, 4)})` : ''}.`,
      reason: a.conflict ? 'conflicto' : 'en-revision',
      question: a.conflict
        ? `¿Cuál es la lista exacta (nombre, nivel, calidad) que entrega hoy ${a.policy.name}? Los ítems en conflicto (${list(conflictNames, 4)}), ¿siguen en el servidor o la guía aprobada es la vigente?`
        : `¿Cuál es la lista exacta (nombre, nivel, calidad) que entrega hoy ${a.policy.name}? ¿Se pueden publicar ${list(unlistedNames, 4)}?`,
      evidence: ['administración', 'configuración'],
      affects: a.unlisted + a.conflict,
    })
  })

  // ── 7. Eventos ───────────────────────────────────────────────────────────
  w.events.forEach((e) => {
    const schedules = w.idx.schedulesByEvent.get(e.id) ?? []
    const rooms = w.idx.roomsByEvent.get(e.id) ?? []
    const base = { category: 'evento' as const, section: 'Eventos', links: ['/guia/eventos'], name: `${e.name} (${e.id})`, evidence: ['administración', 'configuración'] as Evidence[] }
    if (e.activeConfigured === false && e.hasAutomaticSchedule === false) {
      add({ ...base, key: `event-manual:${e.id}`, missing: 'Si se usa de forma manual o debe mostrarse como inactivo', known: '27_events: active_configured = false, has_automatic_schedule = false.', reason: 'falta-dato', question: `${e.name}: ¿el staff lo activa manualmente, o debe seguir presentado como inactivo en la guía?` })
      return
    }
    if (/trigger/i.test(e.timingType ?? '')) {
      const ask = [!rooms.some((r) => r.levelMin !== undefined) && 'requisitos de entrada (nivel, ítem o misión)', 'qué lo activa', 'cada cuánto puede volver a abrirse'].filter(Boolean) as string[]
      add({ ...base, key: `event-trigger:${e.id}`, missing: ask.join(', '), known: `27_events: timing_type = ${e.timingType} (evento por activación, sin horario fijo)${rooms.length ? `; ${rooms.length} salas con niveles en 33_event_rooms` : ''}.`, reason: 'falta-dato', question: `${e.name} se activa por condición, no por horario: ¿${ask.join(', ')}?` })
      return
    }
    const missing: string[] = []
    if (e.activeConfigured === undefined) missing.push('si está activo')
    if (!schedules.length && !e.hasAutomaticSchedule && !e.dailyOccurrences) missing.push('horario (días y horas)')
    if (missing.length) add({ ...base, key: `event:${e.id}`, missing: missing.join(' y '), known: `En 27_events${e.timingType ? ` (timing_type ${e.timingType})` : ''}.`, reason: 'falta-dato', question: `${e.name}: ¿${missing.join('? ¿')}?` })
  })

  // ── 8. Accesos a mapas ───────────────────────────────────────────────────
  const eventMapIds = new Set(Array.from(w.events.values()).flatMap((e) => e.possibleMapIds))
  const noAccess: string[] = []
  const partialAccess: string[] = []
  const eventOnly: string[] = []
  w.maps.forEach((m) => {
    if (!m.moveEntries.length && m.minimumMoveLevel === undefined) (eventMapIds.has(m.id) ? eventOnly : noAccess).push(m.name)
    else if (m.moveEntries.some((e) => e.minLevel === undefined) && m.minimumMoveLevel === undefined) partialAccess.push(m.name)
  })
  if (noAccess.length) add({ key: 'map-access:none', category: 'mapa', section: 'Mapas', links: ['/guia/mapas'], name: `${noAccess.length} mapas sin acceso documentado`, missing: 'Cómo se entra y con qué requisito', known: `Sin minimum_move_level ni move_entries en 07_maps: ${list(noAccess, 10)}.`, reason: 'falta-dato', question: `¿Cómo se entra a ${list(noAccess, 6)} (portal, NPC, /move) y con qué nivel, misión o ítem?`, evidence: ['administración', 'configuración'], affects: noAccess.length })
  if (partialAccess.length) add({ key: 'map-access:partial', category: 'mapa', section: 'Mapas', links: ['/guia/mapas'], name: `${partialAccess.length} mapas con accesos sin nivel`, missing: 'Nivel mínimo de cada acceso', known: `Tienen move_entries sin nivel: ${list(partialAccess, 10)}.`, reason: 'falta-dato', question: `¿Qué nivel mínimo exige cada acceso de ${list(partialAccess, 6)}?`, evidence: ['administración', 'configuración'], affects: partialAccess.length })
  if (eventOnly.length) solvedFromData.push(`${eventOnly.length} mapas sin /move son mapas de evento (27_events); su acceso es el del evento: ${list(eventOnly, 8)}.`)

  // ── 9. Pendientes declarados en 00_control.csv ───────────────────────────
  const ctl = normalizeControlPending(extras.controlPending)
  ctl.forEach((c) => {
    const t = c.text.toLowerCase()
    const tag = `[Declarado también en 00_control.csv: ${c.id}]`
    if (/receta|chaos machine|mezcla|mix/.test(t)) {
      recipes.forEach((r) => (r.variants?.length ? r.variants.map((v) => `recipe:${r.id}:${v.id}`) : [`recipe:${r.id}`]).forEach((k) => appendKnown(k, tag)))
      return
    }
    if (/combo/.test(t) && appendKnown('combo:bk', tag)) return
    if (/npc/.test(t) && /coord|ubicaci|posici/.test(t)) {
      npcs.forEach((n) => n.monsterId !== undefined && appendKnown(`npc-location:${n.monsterId}`, tag))
      return
    }
    if (/recompensa|efecto|reward/.test(t) && /misi|quest/.test(t)) {
      ;['quest-reward:sebina', 'quest-reward:marlon', 'quest-reward:devin'].forEach((k) => appendKnown(k, tag))
      return
    }
    add({ key: `control:${c.id}`, category: 'paquete', section: 'Paquete 08/10/2026', links: ['/guia/verificacion#paquete'], name: c.title ?? c.id, missing: c.text, known: `Declarado en details_json.pending_requirements de la fila dataset.${c.extra ? ` ${c.extra}` : ''}`, reason: 'falta-dato', question: controlQuestion(c.text), evidence: ['administración'] })
  })

  // ── 10. Sistemas y secciones sin datos ───────────────────────────────────
  ;([
    ['clases', 'Clases y habilidades', '¿Estadísticas iniciales, puntos por nivel y habilidades (con nivel de aprendizaje) de cada clase en Vanguard?'],
    ['mejoras', 'Joyas y mejoras de ítems', '¿Tasas de éxito de +10 a +15, qué se pierde al fallar y cómo funcionan Luck y la opción adicional?'],
    ['sistemas-avanzados', 'Socket, Harmony y opciones 380', '¿Están activos Socket, Harmony y las opciones de nivel 380?'],
    ['mascotas', 'Mascotas y monturas', '¿Cómo se obtienen o crean Dark Horse, Dark Raven, Dinorant y Fenrir?'],
    ['master', 'Árbol Master', '¿Desde qué nivel o reset se accede al árbol Master y cuántos puntos se obtienen?'],
    ['helper', 'MU Helper y OffHelper', '¿Requisitos, coste y límites de MU Helper y OffHelper?'],
    ['resets', 'Resets y Grand Reset', '¿Requisitos de reset, qué se conserva y qué premios da cada etapa?'],
    ['market', 'Vanguard Market, VIP y Pase de Batalla', '¿Qué está disponible hoy, qué moneda usa y qué comisiones cobra?'],
    ['comandos', 'Comandos del juego', '¿Qué comandos están activos y qué hace cada uno?'],
  ] as const).forEach(([k, name, question]) => add({ key: `system:${k}`, category: 'sistema', section: 'Secciones por crear', links: ['/guia'], name, missing: 'Información oficial de Vanguard', known: 'No hay datos en el paquete; la sección no se publicó.', reason: 'falta-dato', question, evidence: ['administración'] }))

  out.sort((a, b) => Number(b.needsAdmin) - Number(a.needsAdmin) || categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category))

  // ── Comprobaciones en la página (staff, sin entrar al juego) ─────────────
  const pageChecks: ProposedTest[] = [
    { testId: 'page:map-markers', what: 'Marcadores sobre la imagen de un mapa', how: 'Abrir la ficha de Lorencia y comparar 3 spots con sus coordenadas X/Y escritas debajo', record: 'Mapa, spot y si el marcador cae en la posición de sus coordenadas (sí/no).' },
    { testId: 'page:search-alas', what: 'Buscador: "alas"', how: 'Escribir "alas" en el buscador de la guía', record: 'Primer resultado (debe ser la guía de alas).' },
    { testId: 'page:search-barracks', what: 'Buscador: "cómo entro a Barracks"', how: 'Escribir la frase en el buscador', record: 'Primer resultado (deben ser los accesos y requisitos de Barracks).' },
    { testId: 'page:recipe-links', what: 'Resultados de recetas enlazados por ID', how: 'Abrir /guia/alas/alas-evo-3 y tocar Wing of Storm', record: 'Si abre la ficha 12,36.' },
  ]

  // ── Pruebas dentro del juego ─────────────────────────────────────────────
  const bull = Array.from(w.monsters.values()).find((m) => /bull fighter/i.test(m.name))
  const bullSpawn = bull ? (w.idx.spawnsByMonster.get(bull.id) ?? []).find((s) => s.kind !== 'invasion') : undefined
  const q0 = buildQuestView(w, 0)
  const tests: ProposedTest[] = [
    { testId: 'game:spot-bull-fighter', what: `Ubicación de ${bull?.name ?? 'Bull Fighter'}`, how: bullSpawn ? `Ir a ${mapName(bullSpawn.mapId)} X ${bullSpawn.xMin ?? '?'}–${bullSpawn.xMax ?? '?'} / Y ${bullSpawn.yMin ?? '?'}–${bullSpawn.yMax ?? '?'}` : 'Ir al mapa de su ficha', record: 'Si aparece ahí (sí/no) y captura con coordenadas.' },
    { testId: 'game:quest-0-start', what: 'Inicio de Sebina, primera parte', how: `Hablar con Sebina con nivel ${q0?.levelMin ?? '(del archivo)'}${q0?.zen !== undefined ? ` y ${q0.zen.toLocaleString('es-ES')} Zen` : ''}`, record: 'Nivel y Zen que pide de verdad, mapa y X/Y de Sebina.' },
    { testId: 'game:quest-0-drop', what: 'Scroll of the Emperor con la misión activa', how: 'Cazar un monstruo de los listados en la guía con la misión aceptada', record: 'Monstruo, mapa y si salió.' },
    { testId: 'game:item-jewel-of-chaos', what: 'Drop normal de Jewel of Chaos', how: 'Cazar en un spot de su ficha', record: 'Monstruo, mapa y si salió (sin probabilidades).' },
    { testId: 'game:barracks-access', what: 'Acceso a Barracks', how: 'Intentar entrar con y sin la misión de Devin', record: 'Requisito real y desde dónde se entra.' },
    { testId: 'game:recipe-alas-evo-1', what: 'Mezcla de alas Evo 1', how: 'Colocar la combinación clásica en la Chaos Machine sin confirmar', record: 'Si la acepta, Zen y % mostrados (captura).' },
    { testId: 'game:event-schedule', what: 'Horario de un evento con horario configurado', how: 'Comparar la tecla H con /guia/eventos', record: 'Evento, hora en la guía y hora real.' },
  ]

  const pub = w.publication
  const catalog = {
    raw: pub.rawItems,
    published: w.items.size,
    excluded: pub.excludedItems,
    noRoutesPublished: noRoutes.length,
    noRoutesExcluded: pub.excludedItems.filter((e) => e.routes === 0).length,
  }

  const coverage: SectionCoverage[] = [
    { section: 'NPC', reviewed: true, note: 'Cruzado con 06_monsters y 08_spawns.' },
    { section: 'Misiones 0–6 y evoluciones', reviewed: w.quests.quests.size > 0, note: w.quests.quests.size ? 'Cruzado con 36–42.' : 'NO REVISADO: archivos de misiones no cargados.' },
    { section: 'Ítems, drops y cajas', reviewed: w.routes.length > 0, note: w.routes.length ? `${w.items.size} ítems publicados con la lógica de las fichas.` : 'NO REVISADO: rutas no cargadas.' },
    { section: 'Apariciones en eventos (31, 32)', reviewed: !!extras.eventMonsters && !!extras.eventSpawnRules, note: extras.eventMonsters && extras.eventSpawnRules ? `${evMon.length} filas de 31 y ${evRules.length} de 32.` : 'NO REVISADO: 31 o 32 no se pudieron leer.' },
    { section: 'Invasiones', reviewed: true, note: 'Contraste con la información aprobada.' },
    { section: 'Eventos (27–34)', reviewed: w.events.size > 0, note: 'Actividad, tipo de activación y horario.' },
    { section: 'Accesos a mapas (07_maps)', reviewed: w.maps.size > 0, note: 'minimum_move_level y move_entries.' },
    { section: 'Recetas, alas y capas', reviewed: true, note: 'Identidad de ingredientes y resultados; Zen, éxito y fallo no están en el paquete.' },
    { section: 'Pendientes de 00_control.csv', reviewed: extras.controlPending !== undefined, note: extras.controlPending !== undefined ? `${ctl.length} declarados, consolidados con los existentes.` : 'NO REVISADO: no se pudo leer la fila dataset.' },
    { section: 'Marcadores sobre imágenes de mapa', reviewed: false, note: 'Requiere comprobación visual en la página (lista aparte).' },
  ]

  return { pendings: out, resolvedByAdmin, solvedFromData, pageChecks, tests, catalog, coverage, extrasErrors: extras.errors, annex: { noRoutes, reviewItems, coordItems, partial: Array.from(byReason.values()), ordinaryGaps, byEvent: Array.from(byEvent.entries()) } }
}

type ControlPending = { id: string; title?: string; text: string; extra?: string }

function normalizeControlPending(v: unknown): ControlPending[] {
  if (v === undefined || v === null) return []
  const fromObj = (o: Record<string, unknown>, fallback: string): ControlPending => {
    const pick = (...ks: string[]) => ks.map((k) => o[k]).find((x) => typeof x === 'string' && x) as string | undefined
    const text = pick('description', 'requirement', 'text', 'what', 'detail', 'details', 'missing', 'note') ?? JSON.stringify(o)
    const extraKeys = Object.keys(o).filter((k) => !['id', 'key', 'code', 'name', 'title', 'description', 'requirement', 'text', 'what', 'detail', 'details', 'missing', 'note'].includes(k))
    return { id: pick('id', 'key', 'code') ?? fallback, title: pick('title', 'name'), text, extra: extraKeys.length ? extraKeys.map((k) => `${k}: ${typeof o[k] === 'string' ? o[k] : JSON.stringify(o[k])}`).join(' · ') : undefined }
  }
  if (Array.isArray(v)) return v.map((x, i) => (typeof x === 'string' ? { id: `#${i + 1}`, text: x } : typeof x === 'object' && x ? fromObj(x as Record<string, unknown>, `#${i + 1}`) : { id: `#${i + 1}`, text: String(x) }))
  if (typeof v === 'object') return Object.entries(v as Record<string, unknown>).map(([k, x]) => (typeof x === 'string' ? { id: k, text: x } : typeof x === 'object' && x ? fromObj(x as Record<string, unknown>, k) : { id: k, text: JSON.stringify(x) }))
  return [{ id: '#1', text: String(v) }]
}

function controlQuestion(text: string): string {
  const t = text.toLowerCase()
  if (/vigen|desplegad|deploy|activa en producci/.test(t)) return '¿La configuración exportada el 08/10/2026 es la que está desplegada hoy en el servidor? Si cambió algo después, ¿qué?'
  if (/variante|variant/.test(t) && /misi|quest/.test(t)) return '¿Qué nombre ve el jugador para cada variante de los objetos de misión (por ejemplo @0 y @1 de un mismo ítem)?'
  if (/bonus|temporal|happy|doble/.test(t)) return '¿Qué bonus temporales están activos hoy, en qué horario y qué multiplican?'
  if (/terreno|terrain|imagen|map image|coordenad/.test(t)) return '¿Las imágenes de terreno corresponden a los mapas actuales del servidor y usan la misma orientación de coordenadas?'
  return `Declarado pendiente en el paquete: "${text}". ¿Cuál es el dato vigente?`
}

export type GuidePendings = ReturnType<typeof buildGuidePendings>

export function formatGuidePendings(r: GuidePendings): string {
  const cell = (s: string) => s.replace(/\s+/g, ' ').replace(/\|/g, '/')
  const admin = r.pendings.filter((p) => p.needsAdmin)
  const internal = r.pendings.filter((p) => !p.needsAdmin)
  const byCat = categoryOrder.map((c) => [c, admin.filter((p) => p.category === c).length] as const).filter(([, n]) => n)
  const row = (p: Pending, i: number): string => `| ${i + 1} | ${cell(p.section)} · ${cell(p.links.join(' · '))} | ${cell(p.name)} | ${cell(p.missing)} | ${cell(p.known)} | ${reasonLabel[p.reason]} | ${cell(p.question)} | ${p.evidence.join(', ')} | ${p.key} |`
  const lines = [
    'PENDIENTES DE LA GUÍA MU VANGUARD',
    `Calculado: ${new Date().toLocaleString('es-ES')} · exportación del ${EXPORT_DATE_LABEL}`,
    `PREGUNTAS PARA ADMINISTRACIÓN: ${admin.length} · ${byCat.map(([c, n]) => `${categoryLabel[c]} ${n}`).join(' · ')}`,
    `Problemas internos (los resuelvo yo): ${internal.length} · Respondidos por administración: ${r.resolvedByAdmin.length} · Pruebas en el juego registradas: ${inGameTests.length}`,
    ...(r.extrasErrors.length ? ['', 'ERRORES DE LECTURA DEL INFORME:', ...r.extrasErrors.map((e) => `- ${e}`)] : []),
    '',
    '## CATÁLOGO',
    `01_items: ${r.catalog.raw} ítems · publicados: ${r.catalog.published} · excluidos por filtro de publicación: ${r.catalog.excluded.length}`,
    ...r.catalog.excluded.map((e) => `  - ${e.id}\t${e.name}\t${e.reason} · rutas originales: ${e.routes}`),
    `Sin ruta de drop: ${r.catalog.noRoutesPublished} publicados + ${r.catalog.noRoutesExcluded} excluidos. "Sin ruta de drop" ≠ "no disponible en el servidor" (eso solo se marca con confirmación).`,
    '',
    '## SECCIONES REVISADAS',
    ...r.coverage.map((c) => `- [${c.reviewed ? 'revisada' : 'NO revisada'}] ${c.section}: ${c.note}`),
    '',
    '## RESUELTO CON LOS ARCHIVOS ACTUALES',
    ...(r.solvedFromData.length ? r.solvedFromData.map((s) => `- ${s}`) : ['- nada nuevo']),
    '',
    `## PREGUNTAS PARA ADMINISTRACIÓN (todas: ${admin.length})`,
    '| # | Sección y enlace | Nombre | Dato que falta | Información disponible | Motivo | Pregunta | Evidencia | Clave |',
    '|---|---|---|---|---|---|---|---|---|',
    ...admin.map(row),
    '',
    `## PROBLEMAS INTERNOS DE CONEXIÓN (${internal.length}) — no requieren respuesta`,
    ...internal.map((p) => `- ${p.name}: ${p.known} (clave ${p.key})`),
    '',
    '## RESPONDIDO POR ADMINISTRACIÓN',
    ...(r.resolvedByAdmin.length ? r.resolvedByAdmin.map((x) => `- ${x.p.name}: ${x.answer} (Confirmado por administración, ${formatAdminDate(x.date)})`) : ['- ninguno todavía']),
    '',
    '## COMPROBACIONES EN LA PÁGINA (staff, sin entrar al juego)',
    ...r.pageChecks.map((t, i) => `${i + 1}. ${t.what}\n   Cómo: ${t.how}\n   Registrar: ${t.record}\n   Clave: ${t.testId}`),
    '',
    '## PRUEBAS DENTRO DEL JUEGO (documentado, aún no probado)',
    ...r.tests.map((t, i) => {
      const done = inGameTest(t.testId)
      return `${i + 1}. ${t.what} [${done ? `HECHA ${done.date}: ${done.result} — ${done.observed}` : 'pendiente'}]\n   Cómo: ${t.how}\n   Registrar: ${t.record}\n   Clave: ${t.testId}`
    }),
    '',
    '## ANEXO A — Ítems publicados sin ruta de drop',
    ...r.annex.noRoutes.map((s) => `- ${s}`),
    '',
    '## ANEXO B — Ítems en revisión (objetivos documentados, publicación retenida)',
    ...r.annex.reviewItems.map((s) => `- ${s}`),
    '',
    `## ANEXO C — ${r.annex.coordItems.length} ítems con monstruo y mapa documentados: solo faltan coordenadas o reglas de aparición`,
    ...r.annex.coordItems.map((s) => `- ${s}`),
    '',
    '## ANEXO D — Otros ítems con información parcial, por motivo',
    ...r.annex.partial.flatMap((g) => [`- ${g.ids.length} × ${g.reason}`, `    ${g.names.map((n, i) => `${n} (${g.ids[i]})`).join(', ')}`]),
    '',
    '## ANEXO E — Monstruo + mapa sin posición',
    ...r.annex.byEvent.flatMap(([eid, e]) => [`- Evento ${eid}${e.inactive ? ' (inactivo en la configuración)' : ''}:`, ...e.gaps.map((g) => `    ${g.monster} en ${g.map}: ${Array.from(g.items).slice(0, 6).join(', ')}`)]),
    ...r.annex.ordinaryGaps.map((g) => `- Sin evento: ${g.monster} en ${g.map}: ${Array.from(g.items).slice(0, 6).join(', ')}`),
    '',
    'Cómo responder: indica la clave y la respuesta. Se registra como "Confirmado por administración" con fecha y solo confirma ese dato.',
  ]
  return lines.join('\n')
}
